import React, { useState, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import AssignmentSelectorModal from '../components/AssignmentSelectorModal';
import Tooltip from '../components/Tooltip';
import FEEventSelectorModal from '../components/FEEventSelectorModal';
import TaxonomySelector from '../components/TaxonomySelector';
import { dateToMonDDYYYY, dateTimeToMonDDYYYYWithTime } from '../utils/dateUtils';

const ProjectBuilder = ({ userData }) => {
  const [searchParams] = useSearchParams();
  const command = searchParams.get('command');
  const projectId = searchParams.get('id');
  const isEditMode = command === 'edit' && projectId;
  
  console.log('ProjectBuilder - Component initialized:', { command, projectId, isEditMode });
  
  const [loading, setLoading] = useState(true);
  const [concentrations, setConcentrations] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    shortdescription: '',
    concentration: '',
    otherconcentrations: [],
    taxonomy: [],
    assignedmentor: userData?.account_id || '',
    medialink: '',
    procedures: '',
    additionalresources: '',
    relatedfeevents: []
  });

  // Project types data
  const projectTypes = [
    { label: "Field Experience (Upload)", value: "Field Experience" },
    { label: "Lab (Upload, Survey, Assessment [1-3 questions])", value: "Lab" },
    { label: "Theory (3 Hr Minimum, Short Answer, Survey)", value: "Theory" },
    { label: "Capstone (Survey, Evaluation - verify student completion of capstone)", value: "Capstone" },
    { label: "Concentration (Upload, Survey, Evaluation)", value: "Concentration" }
  ];

  const [selectedProjectTypes, setSelectedProjectTypes] = useState([]);
  const [projectTypeAssignments, setProjectTypeAssignments] = useState({});
  const [allAssignments, setAllAssignments] = useState([]);
  const [assignmentModalConfig, setAssignmentModalConfig] = useState({
    isOpen: false,
    projectType: null,
    selectedAssignments: [],
    onSelectionChange: null
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [criticalDataError, setCriticalDataError] = useState(null);
  const [relatedFEEvents, setRelatedFEEvents] = useState([]);
  const [feEventModalOpen, setFEEventModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [projectTypeHours, setProjectTypeHours] = useState({});
  const [taxonomyItems, setTaxonomyItems] = useState([]);
  
  // Edit mode specific state
  const [hasSubmissions, setHasSubmissions] = useState(false);
  const [projectLoaded, setProjectLoaded] = useState(false);
  const [projectCode, setProjectCode] = useState('');
  const [submissionCount, setSubmissionCount] = useState(0);

  // Fetch existing project data when in edit mode
  const fetchExistingProject = useCallback(async () => {
    console.log('ProjectBuilder - fetchExistingProject called, isEditMode:', isEditMode, 'projectId:', projectId);
    if (!isEditMode || !projectId) {
      console.log('ProjectBuilder - fetchExistingProject early return, not in edit mode or no projectId');
      return;
    }

    try {
      console.log('ProjectBuilder - Getting token...');
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      console.log('ProjectBuilder - Token found, fetching project...');

      const response = await axios.get(`/api/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('ProjectBuilder - Project fetched successfully:', response.data);

      if (!response.data || !response.data.project) {
        throw new Error('Invalid response format from server');
      }

      // Check for existing submissions
      console.log('ProjectBuilder - Checking for submissions...');
      const submissionsResponse = await axios.get(`/api/projectsubmissions/by-project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('ProjectBuilder - Submissions check completed:', submissionsResponse.data);

      if (submissionsResponse.data.submissionCount > 0) {
        // Mark that this project has submissions
        setHasSubmissions(true);
        setSubmissionCount(submissionsResponse.data.submissionCount);
      }

      const existingProject = response.data.project;
      console.log('ProjectBuilder - Processing existing project:', existingProject);

      // Transform the database data to match our component structure
      const transformedProject = {
        title: existingProject.title || '',
        shortdescription: existingProject.shortdescription || '',
        concentration: existingProject.concentration || '',
        otherconcentrations: existingProject.otherconcentrations || [],
        taxonomy: existingProject.taxonomy || [],
        assignedmentor: existingProject.assignedmentor || userData?.account_id || '',
        medialink: existingProject.medialink || '',
        procedures: existingProject.procedures || '',
        additionalresources: existingProject.additionalresources || '',
        relatedfeevents: existingProject.relatedfeevents || []
      };

      console.log('ProjectBuilder - Transformed project data:', transformedProject);

      // Parse JSON fields if they're strings
      if (typeof transformedProject.otherconcentrations === 'string') {
        try {
          transformedProject.otherconcentrations = JSON.parse(transformedProject.otherconcentrations);
        } catch (e) {
          transformedProject.otherconcentrations = [];
        }
      }
      if (typeof transformedProject.taxonomy === 'string') {
        try {
          transformedProject.taxonomy = JSON.parse(transformedProject.taxonomy);
        } catch (e) {
          transformedProject.taxonomy = [];
        }
      }
      if (typeof transformedProject.relatedfeevents === 'string') {
        try {
          transformedProject.relatedfeevents = JSON.parse(transformedProject.relatedfeevents);
        } catch (e) {
          transformedProject.relatedfeevents = [];
        }
      }

      setFormData(transformedProject);
      console.log('ProjectBuilder - Form data set');

      // Set project code for navigation
      if (existingProject.code) {
        setProjectCode(existingProject.code);
      }

      // Set project types and assignments (only if no submissions)
      if (!submissionsResponse.data.submissionCount || submissionsResponse.data.submissionCount === 0) {
        console.log('ProjectBuilder - No submissions, loading project types and assignments...');
        if (existingProject.types && Array.isArray(existingProject.types)) {
          setSelectedProjectTypes(existingProject.types);
          console.log('ProjectBuilder - Project types set:', existingProject.types);
        }

        if (existingProject.typedata && Array.isArray(existingProject.typedata)) {
          const newProjectTypeAssignments = {};
          const newProjectTypeHours = {};

          existingProject.typedata.forEach(typeData => {
            if (typeData.type && typeData.assignments) {
              newProjectTypeAssignments[typeData.type] = [];
              newProjectTypeHours[typeData.type] = typeData.hours || 0;
            }
          });

          setProjectTypeAssignments(newProjectTypeAssignments);
          setProjectTypeHours(newProjectTypeHours);
          console.log('ProjectBuilder - Project type assignments and hours set');

          // Fetch assignments for the existing project types
          try {
            console.log('ProjectBuilder - Fetching assignments...');
            const assignmentsResponse = await axios.get('/api/assignments', {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            const allAssignments = assignmentsResponse.data.assignments || assignmentsResponse.data || [];
            setAllAssignments(allAssignments);
            console.log('ProjectBuilder - Assignments fetched:', allAssignments.length);

            // Populate assignments for each project type
            const populatedAssignments = { ...newProjectTypeAssignments };
            existingProject.typedata.forEach(typeData => {
              if (typeData.type && typeData.assignments && Array.isArray(typeData.assignments)) {
                const typeAssignments = allAssignments.filter(assignment => 
                  typeData.assignments.includes(assignment.id)
                );
                populatedAssignments[typeData.type] = typeAssignments;
              }
            });
            
            setProjectTypeAssignments(populatedAssignments);
            console.log('ProjectBuilder - Project type assignments populated');
          } catch (error) {
            console.error('Error fetching assignments for project types:', error);
          }
        }
      } else {
        console.log('ProjectBuilder - Project has submissions, skipping assignment loading');
      }

      // Set related FE events
      if (existingProject.relatedfeevents && Array.isArray(existingProject.relatedfeevents)) {
        console.log('ProjectBuilder - Loading related FE events...');
        // Fetch FE event details
        try {
          const feEventsResponse = await axios.get('/api/fieldexperienceevents', {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const allFEEvents = feEventsResponse.data.events || [];
          const relatedEvents = allFEEvents.filter(event => 
            existingProject.relatedfeevents.includes(event.id)
          );
          
          setRelatedFEEvents(relatedEvents);
          console.log('ProjectBuilder - Related FE events set:', relatedEvents.length);
        } catch (error) {
          console.error('Error fetching related FE events:', error);
        }
      }

      console.log('ProjectBuilder - Setting projectLoaded to true');
      setProjectLoaded(true);

    } catch (error) {
      console.error('Error fetching existing project:', error);
      setError(error.message || 'Failed to load existing project. Please refresh the page.');
    }
  }, [isEditMode, projectId, userData?.account_id]);

  useEffect(() => {
    console.log('ProjectBuilder - useEffect for concentrations and taxonomy started');
    const fetchConcentrations = async () => {
      try {
        console.log('ProjectBuilder - Fetching concentrations...');
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/concentrations', {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('ProjectBuilder - Concentrations fetched:', response.data.concentrations?.length || 0);
        setConcentrations(response.data.concentrations);
        
        // Check if we have any concentrations
        if (!response.data.concentrations || response.data.concentrations.length === 0) {
          setCriticalDataError('No concentrations found. Please contact your administrator.');
          return;
        }
      } catch (error) {
        console.error('Error fetching concentrations:', error);
        setCriticalDataError('Failed to load concentrations. Please refresh the page.');
      }
    };

    const fetchTaxonomyItems = async () => {
      try {
        console.log('ProjectBuilder - Fetching taxonomy items...');
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/taxonomy/all', {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('ProjectBuilder - Taxonomy items fetched:', response.data.items?.length || 0);
        setTaxonomyItems(response.data.items || []);
      } catch (error) {
        console.error('Error fetching taxonomy items:', error);
        // Don't set error for taxonomy items as they're not critical for basic functionality
      }
    };

    const fetchData = async () => {
      console.log('ProjectBuilder - fetchData started, isEditMode:', isEditMode, 'projectLoaded:', projectLoaded);
      await Promise.all([fetchConcentrations(), fetchTaxonomyItems()]);
      // Only set loading to false if not in edit mode, or if project is loaded
      console.log('ProjectBuilder - fetchData completed, checking if should set loading to false');
      if (!isEditMode || projectLoaded) {
        console.log('ProjectBuilder - Setting loading to false');
        setLoading(false);
      } else {
        console.log('ProjectBuilder - Keeping loading true, waiting for project to load');
      }
    };

    fetchData();
  }, [isEditMode, projectLoaded]);



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };



  const handleProjectTypeChange = (projectType) => {
    setSelectedProjectTypes(prev => 
      prev.includes(projectType)
        ? prev.filter(type => type !== projectType)
        : [...prev, projectType]
    );
    
    // Clear assignments for deselected project type
    if (projectTypeAssignments[projectType]) {
      setProjectTypeAssignments(prev => {
        const newState = { ...prev };
        delete newState[projectType];
        return newState;
      });
    }
  };

  const fetchUserAssignments = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('ProjectBuilder - Token available:', !!token);
      
      if (!token) {
        console.error('ProjectBuilder - No authentication token found');
        return;
      }
      
      console.log('ProjectBuilder - Making API call to /api/assignments');
      const response = await axios.get('/api/assignments', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('ProjectBuilder - Full API response:', response.data);
      console.log('ProjectBuilder - Response status:', response.status);
      
      // The API already filters by user, so we can use all assignments returned
      const allAssignments = response.data.assignments || response.data || [];
      console.log('ProjectBuilder - All assignments from API:', allAssignments);
      console.log('ProjectBuilder - Assignments array length:', allAssignments.length);
      
      // Since the API already filters by user, we don't need to filter again
      // But let's add some debugging to see what we're getting
      if (allAssignments.length > 0) {
        console.log('ProjectBuilder - First assignment sample:', allAssignments[0]);
        console.log('ProjectBuilder - Assignment fields:', Object.keys(allAssignments[0]));
        
        // Check taxonomy data specifically
        allAssignments.forEach((assignment, index) => {
          console.log(`ProjectBuilder - Assignment ${index + 1} (${assignment.title}):`, {
            id: assignment.id,
            taxonomy: assignment.taxonomy,
            taxonomyType: typeof assignment.taxonomy,
            taxonomyLength: Array.isArray(assignment.taxonomy) ? assignment.taxonomy.length : 'N/A',
            otherconcentrations: assignment.otherconcentrations,
            concentration: assignment.concentration
          });
        });
      } else {
        console.log('ProjectBuilder - No assignments returned from API');
      }
      
      setAllAssignments(allAssignments);
    } catch (error) {
      console.error('ProjectBuilder - Error fetching assignments:', error);
      console.error('ProjectBuilder - Error response:', error.response?.data);
      console.error('ProjectBuilder - Error status:', error.response?.status);
    }
  }, []);

  // Fetch assignments when userData is available
  useEffect(() => {
    console.log('ProjectBuilder - userData changed:', userData);
    if (userData?.account_id) {
      console.log('ProjectBuilder - userData.account_id available, fetching assignments');
      fetchUserAssignments();
    } else {
      console.log('ProjectBuilder - userData.account_id not available yet');
    }
  }, [userData?.account_id, fetchUserAssignments]);

  // Fetch existing project data when in edit mode
  useEffect(() => {
    console.log('ProjectBuilder - useEffect for fetchExistingProject triggered, isEditMode:', isEditMode, 'projectId:', projectId, 'userData:', !!userData);
    if (isEditMode) {
      console.log('ProjectBuilder - Calling fetchExistingProject...');
      fetchExistingProject();
    }
  }, [isEditMode, projectId, userData?.account_id, fetchExistingProject]);

  const openAssignmentModal = (projectType) => {
    const currentAssignments = projectTypeAssignments[projectType] || [];
    console.log('ProjectBuilder - Opening assignment modal for project type:', projectType);
    console.log('ProjectBuilder - Current assignments for this type:', currentAssignments);
    console.log('ProjectBuilder - All available assignments:', allAssignments);
    
    setAssignmentModalConfig({
      isOpen: true,
      projectType,
      selectedAssignments: currentAssignments,
      onSelectionChange: (selectedAssignments) => {
        setProjectTypeAssignments(prev => ({
          ...prev,
          [projectType]: selectedAssignments
        }));
      }
    });
  };

  const closeAssignmentModal = () => {
    setAssignmentModalConfig({
      isOpen: false,
      projectType: null,
      selectedAssignments: [],
      onSelectionChange: null
    });
  };

  // Handle taxonomy inheritance from assignments
  const handleTaxonomyInheritance = (assignments) => {
    console.log('ProjectBuilder - Inheriting taxonomy from assignments:', assignments);
    
    // Collect all unique taxonomy items and concentrations from assignments
    const inheritedTaxonomy = [];
    const inheritedConcentrations = new Set();
    
    assignments.forEach(assignment => {
      // Add assignment's primary concentration
      if (assignment.concentration) {
        inheritedConcentrations.add(assignment.concentration);
      }
      
      // Add assignment's other concentrations
      if (assignment.otherconcentrations && Array.isArray(assignment.otherconcentrations)) {
        assignment.otherconcentrations.forEach(conc => {
          if (conc) inheritedConcentrations.add(conc);
        });
      }
      
      // Add assignment's taxonomy items
      if (assignment.taxonomy && Array.isArray(assignment.taxonomy)) {
        assignment.taxonomy.forEach(taxItem => {
          if (taxItem.taxonomy_id) {
            inheritedTaxonomy.push({
              taxonomy_id: taxItem.taxonomy_id,
              parent_id: taxItem.parent_id || null,
              concentration: taxItem.concentration || assignment.concentration
            });
          }
        });
      }
    });
    
    // Remove duplicates from taxonomy (based on taxonomy_id)
    const uniqueTaxonomy = [];
    const seenTaxonomyIds = new Set();
    inheritedTaxonomy.forEach(taxItem => {
      if (!seenTaxonomyIds.has(taxItem.taxonomy_id)) {
        seenTaxonomyIds.add(taxItem.taxonomy_id);
        uniqueTaxonomy.push(taxItem);
      }
    });
    
    // Convert concentrations Set to array and filter out the primary concentration
    const otherConcentrationsArray = Array.from(inheritedConcentrations).filter(
      concId => concId !== formData.concentration
    );
    
    console.log('ProjectBuilder - Inherited data:', {
      taxonomy: uniqueTaxonomy,
      otherConcentrations: otherConcentrationsArray
    });
    
    // Update form data with inherited values
    setFormData(prev => ({
      ...prev,
      taxonomy: uniqueTaxonomy,
      otherconcentrations: otherConcentrationsArray
    }));
    
    // Show success message
    setSuccess('Taxonomy and concentrations inherited from assignments!');
    setTimeout(() => setSuccess(''), 3000);
  };





  const getAssignmentTooltipContent = (assignment) => {
    let content = '';
    if (assignment.title) {
      content += `Title: ${assignment.title}`;
    }
    if (assignment.hours) {
      if (content) content += '\n';
      content += `Hours: ${assignment.hours}`;
    }
    if (assignment.gradingresulttype) {
      if (content) content += '\n';
      content += `Type: ${assignment.gradingresulttype}`;
    }
    if (assignment.questions && assignment.questions.length > 0) {
      if (content) content += '\n';
      content += `Questions: ${assignment.questions.length}`;
    }
    if (assignment.createddate) {
      if (content) content += '\n';
      content += `Created: ${dateTimeToMonDDYYYYWithTime(assignment.createddate, true)}`;
    }
    if (assignment.updateddate) {
      if (content) content += '\n';
      content += `Updated: ${dateTimeToMonDDYYYYWithTime(assignment.updateddate, true)}`;
    }
    if (!content) {
      content = 'No assignment details available';
    }
    return content;
  };

  const getProjectTypeValidationStatus = (projectType, assignments) => {
    const sumHours = assignments.reduce((sum, assignment) => sum + (assignment.hours || 0), 0);
    
    switch (projectType) {
      case "Field Experience": {
        const hasUpload = assignments.some(assignment => 
          assignment.questions && assignment.questions.some(q => q.type === "upload")
        );
        return hasUpload ? "✓ Valid: Has upload assignment" : "⚠ Missing: Upload assignment required";
      }
      
      case "Lab": {
        const surveys = assignments.filter(assignment => assignment.gradingresulttype === "Survey");
        const assessments = assignments.filter(assignment => assignment.gradingresulttype !== "Survey");
        const hasUpload = assignments.some(assignment => 
          assignment.questions && assignment.questions.some(q => q.type === "upload")
        );
        
        const surveyValid = surveys.length > 0 && surveys.some(s => s.questions && s.questions.length >= 3);
        const assessmentValid = assessments.length > 0 && assessments.some(a => a.questions && a.questions.length >= 3);
        
        const issues = [];
        if (!surveyValid) issues.push("Survey (3+ questions)");
        if (!assessmentValid) issues.push("Assessment (3+ questions)");
        if (!hasUpload) issues.push("Upload");
        
        return issues.length === 0 ? "✓ Valid: All requirements met" : `⚠ Missing: ${issues.join(", ")}`;
      }
      
      case "Theory": {
        const hasOpenAnswer = assignments.some(assignment => 
          assignment.questions && assignment.questions.some(q => q.type === "openAnswer")
        );
        const surveys = assignments.filter(assignment => assignment.gradingresulttype === "Survey");
        const surveyValid = surveys.length > 0 && surveys.some(s => s.questions && s.questions.length >= 3);
        const hoursValid = sumHours >= 3;
        
        const issues = [];
        if (!hoursValid) issues.push("3+ hours");
        if (!hasOpenAnswer) issues.push("Open Answer");
        if (!surveyValid) issues.push("Survey (3+ questions)");
        
        return issues.length === 0 ? "✓ Valid: All requirements met" : `⚠ Missing: ${issues.join(", ")}`;
      }
      
      case "Capstone": {
        const surveys = assignments.filter(assignment => assignment.gradingresulttype === "Survey");
        const surveyValid = surveys.length > 0 && surveys.some(s => s.questions && s.questions.length >= 3);
        
        return surveyValid ? "✓ Valid: Has survey with 3+ questions" : "⚠ Missing: Survey with 3+ questions";
      }
      
      case "Concentration": {
        const surveys = assignments.filter(assignment => assignment.gradingresulttype === "Survey");
        const surveyValid = surveys.length > 0 && surveys.some(s => s.questions && s.questions.length >= 3);
        const hasUpload = assignments.some(assignment => 
          assignment.questions && assignment.questions.some(q => q.type === "upload")
        );
        
        const issues = [];
        if (!surveyValid) issues.push("Survey (3+ questions)");
        if (!hasUpload) issues.push("Upload");
        
        return issues.length === 0 ? "✓ Valid: All requirements met" : `⚠ Missing: ${issues.join(", ")}`;
      }
      
      default:
        return "Unknown project type";
    }
  };

  // Helper to render video from URL
  const renderVideoFromUrl = (url) => {
    if (!url) return null;
    
    // Try to embed Vimeo/YouTube, fallback to HTML5 video
    if (/vimeo\.com/.test(url)) {
      const id = url.split('/').pop();
      return <iframe src={`https://player.vimeo.com/video/${id}`} width="320" height="180" frameBorder="0" allow="autoplay; fullscreen" allowFullScreen title="Vimeo Video" />;
    }
    if (/youtube\.com|youtu\.be/.test(url)) {
      let id = url;
      if (url.includes('youtu.be/')) id = url.split('youtu.be/')[1];
      else if (url.includes('v=')) id = url.split('v=')[1].split('&')[0];
      return <iframe width="320" height="180" src={`https://www.youtube.com/embed/${id}`} frameBorder="0" allow="autoplay; encrypted-media" allowFullScreen title="YouTube Video" />;
    }
    // Fallback: HTML5 video
    return <video src={url} width="320" height="180" controls />;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent submission if project has submissions
    if (isEditMode && hasSubmissions) {
      setError('This project cannot be updated because it has existing submissions.');
      return;
    }
    
    setError('');
    setSuccess('');
    setSubmitting(true);
    setSubmissionStatus('submitting');

    // Validate that critical data is loaded
    if (criticalDataError || !concentrations || concentrations.length === 0) {
      setError('Critical data (concentrations) is not loaded properly. Please refresh the page and try again.');
      setSubmitting(false);
      setSubmissionStatus('idle');
      return;
    }

    // Validate required fields
    if (!formData.title.trim()) {
      setError('Title is required');
      setSubmitting(false);
      setSubmissionStatus('idle');
      return;
    }
    if (!formData.shortdescription.trim()) {
      setError('Short description is required');
      setSubmitting(false);
      setSubmissionStatus('idle');
      return;
    }
    if (!formData.concentration) {
      setError('Concentration is required');
      setSubmitting(false);
      setSubmissionStatus('idle');
      return;
    }
    if (!formData.medialink.trim()) {
      setError('Media link is required');
      setSubmitting(false);
      setSubmissionStatus('idle');
      return;
    }
    if (!formData.procedures.trim()) {
      setError('Procedures are required');
      setSubmitting(false);
      setSubmissionStatus('idle');
      return;
    }

    if (selectedProjectTypes.length === 0) {
      setError('At least 1 Project Type is required');
      setSubmitting(false);
      setSubmissionStatus('idle');
      return;
    }

    // Validate taxonomy requirements
    if (!formData.taxonomy || formData.taxonomy.length === 0) {
      setError('At least 1 taxonomy item is required (standards, skills, or objectives)');
      setSubmitting(false);
      setSubmissionStatus('idle');
      return;
    }

    // Check if taxonomy includes at least 1 standard, skill, and objective
    // The taxonomy array contains objects with taxonomy_id, we need to map to actual taxonomy items
    if (formData.taxonomy.length === 0) {
      setError('At least 1 taxonomy item is required (standards, skills, or objectives)');
      setSubmitting(false);
      setSubmissionStatus('idle');
      return;
    }

    // Map taxonomy_id to actual taxonomy items to check their types
    console.log('ProjectBuilder - formData.taxonomy:', formData.taxonomy);
    console.log('ProjectBuilder - taxonomyItems:', taxonomyItems);
    
    const taxonomyTypes = formData.taxonomy.map(taxonomyItem => {
      const fullTaxonomyItem = taxonomyItems.find(item => item.id === taxonomyItem.taxonomy_id);
      console.log('ProjectBuilder - mapping taxonomyItem:', taxonomyItem, 'to fullTaxonomyItem:', fullTaxonomyItem);
      return fullTaxonomyItem ? fullTaxonomyItem.type : null;
    }).filter(type => type !== null);
    
    console.log('ProjectBuilder - taxonomyTypes:', taxonomyTypes);

    const hasStandard = taxonomyTypes.includes('Standard');
    const hasSkill = taxonomyTypes.includes('Skill');
    const hasObjective = taxonomyTypes.includes('Objective');

    if (!hasStandard || !hasSkill || !hasObjective) {
      const missingTypes = [];
      if (!hasStandard) missingTypes.push('Standard');
      if (!hasSkill) missingTypes.push('Skill');
      if (!hasObjective) missingTypes.push('Objective');
      
      setError(`Taxonomy must include at least 1 of each: Standard, Skill, and Objective. Missing: ${missingTypes.join(', ')}`);
      setSubmitting(false);
      setSubmissionStatus('idle');
      return;
    }

    // Validate project type requirements
    const projectTypeErrors = [];
    
    selectedProjectTypes.forEach(projectType => {
      const attachedAssignments = projectTypeAssignments[projectType] || [];
      const sumHoursOfAssignments = attachedAssignments.reduce((sum, assignment) => sum + (assignment.hours || 0), 0);
      
      switch (projectType) {
        case "Field Experience": {
          // Must have an Upload
          const assignmentsWithUploads = attachedAssignments.filter(assignment => 
            assignment.questions && assignment.questions.some(q => q.type === "upload")
          );
          if (assignmentsWithUploads.length === 0) {
            projectTypeErrors.push("Field Experience: Must have an Upload assignment");
          }
          break;
        }
        
        case "Lab": {
          // Must have Upload, Survey (min 3 questions), Assessment (min 3 questions)
          const surveys = attachedAssignments.filter(assignment => assignment.gradingresulttype === "Survey");
          if (surveys.length === 0) {
            projectTypeErrors.push("Lab: Must have a Survey assignment");
          } else if (surveys.filter(s => s.questions && s.questions.length >= 3).length === 0) {
            projectTypeErrors.push("Lab: Must have a Survey with at least 3 questions");
          }
          
          const assessments = attachedAssignments.filter(assignment => assignment.gradingresulttype !== "Survey");
          if (assessments.length === 0) {
            projectTypeErrors.push("Lab: Must have an Assessment assignment");
          } else if (assessments.filter(a => a.questions && a.questions.length >= 3).length === 0) {
            projectTypeErrors.push("Lab: Must have an Assessment with at least 3 questions");
          }
          
          const assignmentsWithUploads = attachedAssignments.filter(assignment => 
            assignment.questions && assignment.questions.some(q => q.type === "upload")
          );
          if (assignmentsWithUploads.length === 0) {
            projectTypeErrors.push("Lab: Must have an Upload assignment");
          }
          break;
        }
        
        case "Theory": {
          // Must have 3+ hours, Short Answer, Survey (min 3 questions)
          if (sumHoursOfAssignments < 3) {
            projectTypeErrors.push("Theory: Must have at least 3 hours total");
          }
          
          const assignmentsWithOpenAnswer = attachedAssignments.filter(assignment => 
            assignment.questions && assignment.questions.some(q => q.type === "openAnswer")
          );
          if (assignmentsWithOpenAnswer.length === 0) {
            projectTypeErrors.push("Theory: Must have a short answer (Open Answer) assignment");
          }
          
          const surveys = attachedAssignments.filter(assignment => assignment.gradingresulttype === "Survey");
          if (surveys.length === 0) {
            projectTypeErrors.push("Theory: Must have a Survey assignment");
          } else if (surveys.filter(s => s.questions && s.questions.length >= 3).length === 0) {
            projectTypeErrors.push("Theory: Must have a Survey with at least 3 questions");
          }
          break;
        }
        
        case "Capstone": {
          // Must have Survey (min 3 questions)
          const surveys = attachedAssignments.filter(assignment => assignment.gradingresulttype === "Survey");
          if (surveys.length === 0) {
            projectTypeErrors.push("Capstone: Must have a Survey assignment");
          } else if (surveys.filter(s => s.questions && s.questions.length >= 3).length === 0) {
            projectTypeErrors.push("Capstone: Must have a Survey with at least 3 questions");
          }
          break;
        }
        
        case "Concentration": {
          // Must have Upload, Survey (min 3 questions)
          const surveys = attachedAssignments.filter(assignment => assignment.gradingresulttype === "Survey");
          if (surveys.length === 0) {
            projectTypeErrors.push("Concentration: Must have a Survey assignment");
          } else if (surveys.filter(s => s.questions && s.questions.length >= 3).length === 0) {
            projectTypeErrors.push("Concentration: Must have a Survey with at least 3 questions");
          }
          
          const assignmentsWithUploads = attachedAssignments.filter(assignment => 
            assignment.questions && assignment.questions.some(q => q.type === "upload")
          );
          if (assignmentsWithUploads.length === 0) {
            projectTypeErrors.push("Concentration: Must have an Upload assignment");
          }
          break;
        }
      }
    });
    
    if (projectTypeErrors.length > 0) {
      setError(`Project type validation errors:\n${projectTypeErrors.join('\n')}`);
      setSubmitting(false);
      setSubmissionStatus('idle');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      // Filter out the main concentration from otherconcentrations
      const filteredOtherConcentrations = formData.otherconcentrations.filter(
        concentrationId => concentrationId !== formData.concentration
      );

      const projectData = {
        createdby: userData?.account_id,
        updatehistory: [],
        title: formData.title,
        shortdescription: formData.shortdescription,
        concentration: formData.concentration,
        otherconcentrations: filteredOtherConcentrations,
        assignedmentor: formData.assignedmentor,
        taxonomy: formData.taxonomy,
        medialink: formData.medialink,
        procedures: formData.procedures,
        additionalresources: formData.additionalresources,
        relatedfeevents: formData.relatedfeevents,
        types: selectedProjectTypes,
        typedata: selectedProjectTypes.map(type => {
          const assignments = projectTypeAssignments[type] || [];
          const sumHours = assignments.reduce((sum, assignment) => sum + (assignment.hours || 0), 0);
          let hours = projectTypeHours[type] !== undefined ? projectTypeHours[type] : sumHours;
          if (hours < sumHours || hours === 0) hours = sumHours; // enforce on backend too
          return {
            type,
            assignments: assignments.map(a => a.id),
            hours: hours
          };
        })
      };

      console.log('Project data to be submitted:', JSON.stringify(projectData, null, 2));

      let response;
      if (isEditMode) {
        // Update existing project
        response = await axios.put(`/api/projects/${projectId}`, projectData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Project updated successfully!');
      } else {
        // Create new project
        response = await axios.post('/api/projects', projectData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Project created successfully!');
      }

      setSubmissionStatus('success');
      
      // Reset form after successful submission (only for create mode)
      if (!isEditMode) {
        setFormData({
          title: '',
          shortdescription: '',
          concentration: '',
          otherconcentrations: [],
          taxonomy: [],
          assignedmentor: userData?.account_id || '',
          medialink: '',
          procedures: '',
          additionalresources: '',
          relatedfeevents: []
        });
        setSelectedProjectTypes([]);
        setProjectTypeAssignments({});
        setRelatedFEEvents([]);
      }
      
      // Hide overlay after showing success message
      if (isEditMode) {
        // In edit mode, show success message for 5 seconds then close
        setTimeout(() => {
          setSubmitting(false);
          setSubmissionStatus('idle');
        }, 5000);
      }
      // In create mode, don't auto-hide the popup - let user click the button
      
      console.log('Project operation response:', response.data);
    } catch (error) {
      console.error('Error with project operation:', error);
      setError(error.response?.data?.error || 'Failed to process project');
      setSuccess('');
      setSubmissionStatus('error');
      // Auto-hide error popup after 5 seconds
      setTimeout(() => {
        setSubmitting(false);
        setSubmissionStatus('idle');
      }, 5000);
    }
  };

  if (loading) {
    console.log('ProjectBuilder - Rendering LoadingSpinner, loading:', loading, 'isEditMode:', isEditMode, 'projectLoaded:', projectLoaded);
    return <LoadingSpinner />;
  }

  console.log('ProjectBuilder - Rendering main component, loading:', loading, 'isEditMode:', isEditMode, 'projectLoaded:', projectLoaded);

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Critical Data Error State */}
        {criticalDataError && (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            backgroundColor: '#f8d7da',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #f5c6cb',
            color: '#721c24'
          }}>
            <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>
              ⚠️ Critical Data Error
            </div>
            <div style={{ fontSize: '14px', marginBottom: '16px' }}>
              {criticalDataError}
            </div>
            <div style={{ fontSize: '14px', marginBottom: '16px', fontWeight: '500' }}>
              You cannot create or edit projects until this issue is resolved.
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
            >
              🔄 Reload Page
            </button>
          </div>
        )}

        {!hasSubmissions && !criticalDataError ? (
          <>
            <h1 style={styles.title}>
              {isEditMode ? 'Edit Project' : 'Project Builder'}
            </h1>
            <p style={styles.subtitle}>
              {isEditMode 
                ? hasSubmissions 
                  ? `This project has ${submissionCount} submission(s) and is displayed in read-only mode.`
                  : 'Edit the project information below. Projects with existing submissions will be displayed in read-only mode.'
                : 'Create a new project by filling out the setup information below.'
              }
            </p>

            {!hasSubmissions && (
              <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.formGroup}>
                  <label htmlFor="title" style={styles.label}>
                    Project Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    style={styles.input}
                    placeholder="Enter project title"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label htmlFor="shortdescription" style={styles.label}>
                    Short Description *
                  </label>
                  <textarea
                    id="shortdescription"
                    name="shortdescription"
                    value={formData.shortdescription}
                    onChange={handleInputChange}
                    required
                    style={styles.textarea}
                    placeholder="Enter a brief description of the project"
                    rows={4}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label htmlFor="concentration" style={styles.label}>
                    Primary Concentration *
                  </label>
                  <select
                    id="concentration"
                    name="concentration"
                    value={formData.concentration}
                    onChange={handleInputChange}
                    required
                    style={styles.select}
                  >
                    <option value="">Select a concentration</option>
                    {concentrations.map((concentration) => (
                      <option key={concentration.id} value={concentration.id}>
                        {concentration.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Taxonomy Selection */}
                <div style={styles.taxonomySection}>
                  <h3 style={styles.sectionTitle}>Concentrations, Skills, Standards, Objectives</h3>
                  <div style={{ color: '#1D4E89', fontSize: '0.98rem', marginBottom: 12, fontWeight: 500 }}>
                    Select other concentrations (if any) involved in this project. Also select any relevant skills, standards, or objectives.
                  </div>
                  <TaxonomySelector
                    selectedTaxonomy={formData.taxonomy || []}
                    onTaxonomyChange={(taxonomy) => setFormData(prev => ({ ...prev, taxonomy }))}
                    concentrations={concentrations}
                    disabled={false}
                    primaryConcentration={formData.concentration}
                    onSelectedConcentrationsChange={(concentrations) => setFormData(prev => ({ ...prev, otherconcentrations: concentrations }))}
                    isStudentRole={false}
                    taxonomyItems={taxonomyItems}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label htmlFor="assignedmentor" style={styles.label}>
                    Assigned Mentor
                  </label>
                  <input
                    type="text"
                    id="assignedmentor"
                    name="assignedmentor"
                    value={userData ? `${userData.first_name} ${userData.last_name}` : ''}
                    disabled
                    style={styles.disabledInput}
                    placeholder="Loading user data..."
                  />
                  <small style={styles.helpText}>
                    This field is automatically set to your account and cannot be changed.
                  </small>
                </div>

                <div style={styles.formGroup}>
                  <label htmlFor="medialink" style={styles.label}>
                    Intro Video Link *
                  </label>
                  <input
                    type="url"
                    id="medialink"
                    name="medialink"
                    value={formData.medialink}
                    onChange={handleInputChange}
                    required
                    style={styles.input}
                    placeholder="Enter YouTube, Vimeo, or MP4 video URL"
                  />
                  <small style={styles.helpText}>
                    Supports YouTube, Vimeo, and direct MP4 video URLs
                  </small>
                  {formData.medialink && (
                    <div style={styles.videoPreview}>
                      {renderVideoFromUrl(formData.medialink)}
                    </div>
                  )}
                </div>

                <div style={styles.formGroup}>
                  <label htmlFor="procedures" style={styles.label}>
                    Procedures *
                  </label>
                  <div style={styles.richTextContainer}>
                    <ReactQuill
                      theme="snow"
                      value={formData.procedures}
                      onChange={(value) => setFormData(prev => ({ ...prev, procedures: value }))}
                      style={styles.richTextEditor}
                      placeholder="Enter project procedures..."
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label htmlFor="additionalresources" style={styles.label}>
                    Additional Resources
                  </label>
                  <div style={styles.richTextContainer}>
                    <ReactQuill
                      theme="snow"
                      value={formData.additionalresources}
                      onChange={(value) => setFormData(prev => ({ ...prev, additionalresources: value }))}
                      style={styles.richTextEditor}
                      placeholder="Enter additional resources, links, or helpful information..."
                    />
                  </div>
                </div>

                {/* Project Types Section */}
                <div style={styles.taxonomySection}>
                  <h3 style={styles.sectionTitle}>Project Types</h3>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Select Project Types
                    </label>
                    <div style={styles.checkboxGroup}>
                      {projectTypes.map((projectType) => (
                        <label key={projectType.value} style={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={selectedProjectTypes.includes(projectType.value)}
                            onChange={() => handleProjectTypeChange(projectType.value)}
                            style={styles.checkbox}
                          />
                          {projectType.label}
                        </label>
                      ))}
                    </div>
                    <small style={styles.helpText}>
                      Select the project types that apply to this project (at least 1 required)
                    </small>
                  </div>

                  {/* Project Type Assignment Containers */}
                  {selectedProjectTypes.map((projectType) => {
                    const assignments = projectTypeAssignments[projectType] || [];
                    const sumHours = assignments.reduce((sum, assignment) => sum + (assignment.hours || 0), 0);
                    const hoursValue = projectTypeHours[projectType] !== undefined ? projectTypeHours[projectType] : sumHours;
                    return (
                      <div key={projectType} style={styles.taxonomyContainer}>
                        <div style={styles.taxonomyHeader}>
                          <h4 style={styles.taxonomyTitle}>{projectType}</h4>
                          <button
                            type="button"
                            style={styles.selectButton}
                            onClick={() => openAssignmentModal(projectType)}
                          >
                            Select Assignments
                          </button>
                        </div>
                        <div style={styles.selectedItems}>
                          {(!assignments || assignments.length === 0) ? (
                            <p style={styles.noItemsText}>
                              No assignments selected (select assignments to meet project type requirements)
                            </p>
                          ) : (
                            <div style={styles.itemsList}>
                              {assignments.map((assignment, index) => (
                                <Tooltip key={assignment.id} content={getAssignmentTooltipContent(assignment)}>
                                  <span style={styles.selectedItem}>
                                    {assignment.title}
                                  </span>
                                </Tooltip>
                              ))}
                            </div>
                          )}
                          {/* Validation Status */}
                          {assignments && assignments.length > 0 && (
                            <div style={styles.validationStatus}>
                              <small style={styles.validationText}>
                                {getProjectTypeValidationStatus(projectType, assignments)}
                              </small>
                            </div>
                          )}
                          {/* Total Hours Input */}
                          {assignments && assignments.length > 0 && (
                            <div style={{ marginTop: 12 }}>
                              <label style={{ fontWeight: 500, marginRight: 8 }}>
                                Total Hours rewarded for Project Completion:
                              </label>
                              <input
                                type="number"
                                min={sumHours || 1}
                                value={hoursValue}
                                onChange={e => {
                                  let val = parseFloat(e.target.value);
                                  if (isNaN(val)) val = sumHours;
                                  setProjectTypeHours(prev => ({ ...prev, [projectType]: val }));
                                }}
                                style={{ width: 80, padding: '0.25rem 0.5rem', borderRadius: 4, border: '1px solid #ccc', marginRight: 8 }}
                              />
                              <span style={{ color: '#888', fontSize: 13 }}>
                                (Sum of assignments: {sumHours})
                              </span>
                              {hoursValue < sumHours || hoursValue === 0 ? (
                                <span style={{ color: 'red', marginLeft: 8, fontSize: 13 }}>
                                  Must be at least {sumHours} and not 0
                                </span>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Related Field Experience Events Section */}
                <div style={styles.taxonomySection}>
                  <h3 style={styles.sectionTitle}>Related Field Experience Events</h3>
                  <div style={styles.taxonomyContainer}>
                    <div style={styles.taxonomyHeader}>
                      <h4 style={styles.taxonomyTitle}>Field Experience Events</h4>
                      <button
                        type="button"
                        onClick={() => setFEEventModalOpen(true)}
                        style={styles.selectButton}
                      >
                        Select Events
                      </button>
                    </div>
                    <div style={styles.selectedItems}>
                      {relatedFEEvents.length === 0 ? (
                        <p style={styles.noItemsText}>No events selected (optional)</p>
                      ) : (
                        <div style={{ 
                          ...styles.itemsList, 
                          overflowX: 'auto', 
                          overflowY: 'auto', 
                          flexWrap: 'wrap',
                          maxHeight: '200px',
                          padding: '8px',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          backgroundColor: '#f8f9fa'
                        }}>
                          {relatedFEEvents.map((event, idx) => (
                            <Tooltip key={event.id} content={`Title: ${event.title}\nDate: ${dateToMonDDYYYY(event.date, false)}\nType: ${event.type}\nStatus: ${event.status}`}>
                              <span style={{
                                ...styles.selectedItem,
                                background: '#e6f7ff',
                                color: '#1D4E89',
                                border: '1px solid #1D4E89',
                                marginRight: 8,
                                marginBottom: 8,
                                cursor: 'pointer',
                                minWidth: 120,
                                textAlign: 'center',
                                display: 'inline-block',
                                fontWeight: 500,
                                padding: '6px 12px'
                              }}>
                                {event.title} <span style={{ color: '#888', fontWeight: 400 }}>({event.date})</span>
                              </span>
                            </Tooltip>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {error && (
                  <div style={styles.errorMessage}>
                    {error}
                  </div>
                )}

                {success && (
                  <div style={styles.successMessage}>
                    {success}
                  </div>
                )}

                <div style={styles.buttonGroup}>
                  <button 
                    type="submit" 
                    style={styles.submitButton}
                  >
                    {isEditMode ? 'Update Project' : 'Create Project'}
                  </button>
                  <button type="button" style={styles.cancelButton} onClick={() => window.history.back()}>
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </>
        ) : null}

        {isEditMode && hasSubmissions && (
          <div className="fullscreen-overlay" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 9999
          }}>
            <div style={{
              background: 'white',
              padding: '40px',
              borderRadius: '12px',
              textAlign: 'center',
              maxWidth: '500px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
              <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Critical Error</h2>
              <p style={{ color: '#374151', marginBottom: '24px', fontSize: '16px', lineHeight: '1.5' }}>
                This project has {submissionCount || 'unknown'} submission(s) and currently may not be updated. (Function in development). Contact Support if you need a change now.
              </p>
              {projectCode && (
                <button 
                  onClick={() => window.location.href = `/projects-directory?command=preview&project_code=${projectCode}`}
                  style={{
                    background: '#1D4E89',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  View Project in Directory
                </button>
              )}
            </div>
          </div>
        )}



        {/* Assignment Selector Modal */}
        <AssignmentSelectorModal
          isOpen={assignmentModalConfig.isOpen}
          onClose={closeAssignmentModal}
          projectType={assignmentModalConfig.projectType}
          selectedAssignments={assignmentModalConfig.selectedAssignments}
          onSelectionChange={assignmentModalConfig.onSelectionChange}
          userAssignments={allAssignments}
          userData={userData}
          onRefreshAssignments={fetchUserAssignments}
          allowMultiple={true}
          isProjectBuilder={true}
          onTaxonomyInheritance={handleTaxonomyInheritance}
        />

        {/* FE Event Selector Modal */}
        <FEEventSelectorModal
          isOpen={feEventModalOpen}
          onClose={() => setFEEventModalOpen(false)}
          selectedEvents={relatedFEEvents}
          onSelectionChange={events => {
            setRelatedFEEvents(events);
            setFormData(prev => ({ ...prev, relatedfeevents: events.map(e => e.id) }));
          }}
          userData={userData}
        />

        {/* Submission Loading Overlay */}
        {submitting && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(2px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              textAlign: 'center',
              padding: '20px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e0e0e0',
              maxWidth: '400px',
              width: '90%'
            }}>
              {submissionStatus === 'submitting' && (
                <>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #1976d2',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px auto'
                  }}></div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                    {isEditMode ? 'Updating your project...' : 'Creating your project...'}
                  </div>
                </>
              )}
              {submissionStatus === 'success' && (
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#28a745', textAlign: 'center' }}>
                  <div style={{ marginBottom: '16px' }}>
                    ✅ Project {isEditMode ? 'updated' : 'created'} successfully!
                  </div>
                  {!isEditMode && (
                    <button
                      onClick={() => window.location.reload()}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#1D4E89',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#0f3a6b'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#1D4E89'}
                    >
                      Make a new project?
                    </button>
                  )}
                  {isEditMode && (
                    <div style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                      This popup will close automatically in a few seconds...
                    </div>
                  )}
                </div>
              )}
              {submissionStatus === 'error' && (
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#dc3545' }}>
                  <div style={{ marginBottom: '16px' }}>
                    ❌ Error occurred. Please try again.
                  </div>
                  <button
                    onClick={() => {
                      setSubmitting(false);
                      setSubmissionStatus('idle');
                    }}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: 'calc(100vh - 64px)',
    backgroundColor: '#f8f9fa',
    padding: '2rem 0'
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '0 2rem'
  },
  title: {
    color: '#1D4E89',
    fontSize: '2.5rem',
    marginBottom: '0.5rem',
    textAlign: 'center'
  },
  subtitle: {
    color: '#666',
    fontSize: '1.1rem',
    marginBottom: '2rem',
    textAlign: 'center'
  },
  form: {
    backgroundColor: 'white',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
  },
  formGroup: {
    marginBottom: '1.5rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '600',
    color: '#333'
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    resize: 'vertical',
    minHeight: '100px',
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    backgroundColor: 'white',
    boxSizing: 'border-box'
  },
  disabledInput: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    backgroundColor: '#f5f5f5',
    color: '#666',
    cursor: 'not-allowed',
    boxSizing: 'border-box'
  },
  helpText: {
    display: 'block',
    marginTop: '0.25rem',
    fontSize: '0.875rem',
    color: '#666'
  },
  buttonGroup: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    marginTop: '2rem'
  },
  submitButton: {
    padding: '0.75rem 2rem',
    backgroundColor: '#1D4E89',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  cancelButton: {
    padding: '0.75rem 2rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  errorMessage: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    border: '1px solid #f5c6cb'
  },
  successMessage: {
    backgroundColor: '#d4edda',
    color: '#155724',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    border: '1px solid #c3e6cb'
  },
  warningMessage: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    border: '1px solid #ffeeba'
  },
  taxonomySection: {
    marginTop: '2rem',
    marginBottom: '2rem'
  },
  sectionTitle: {
    color: '#1D4E89',
    fontSize: '1.5rem',
    marginBottom: '1.5rem',
    borderBottom: '2px solid #e0e0e0',
    paddingBottom: '0.5rem'
  },
  taxonomyContainer: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '1.5rem'
  },
  taxonomyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem'
  },
  taxonomyTitle: {
    color: '#333',
    fontSize: '1.25rem',
    margin: 0,
    fontWeight: '600'
  },
  selectButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1D4E89',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'background-color 0.2s'
  },
  selectedItems: {
    minHeight: '60px'
  },
  noItemsText: {
    color: '#666',
    fontStyle: 'italic',
    margin: 0
  },
  itemsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem'
  },
  selectedItem: {
    backgroundColor: '#e3f2fd',
    color: '#1D4E89',
    padding: '0.125rem 0.5rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: '500',
    border: '1px solid #bbdefb',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block'
  },
  videoPreview: {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    border: '1px solid #e0e0e0'
  },
  richTextContainer: {
    border: '1px solid #ddd',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  richTextEditor: {
    height: '150px'
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    maxHeight: '200px',
    overflowY: 'auto',
    padding: '0.5rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: '#f8f9fa'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#333'
  },
  checkbox: {
    margin: 0,
    cursor: 'pointer'
  },
  validationStatus: {
    marginTop: '0.5rem',
    padding: '0.5rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    border: '1px solid #e0e0e0'
  },
  validationText: {
    color: '#666',
    fontSize: '0.75rem',
    lineHeight: '1.2'
  }
};

export default ProjectBuilder; 